/**
 * What these helpers need from a theme: the palette. Structural rather than MUI's `Theme`
 * for the reason `brandGradient.ts` sets out at length — this monorepo resolves
 * `@mui/material` twice, so the two `Theme` types are nominally distinct and a
 * `packages/ui` helper that demands one cannot be called from an app without a cast.
 */
export type MediaPlateThemeLike = {
  palette: { mode: "light" | "dark" } & Record<string, any>;
  shape?: { borderRadius?: number | string };
};

/**
 * The one source for the PRODUCT MEDIA PLATE — the surface a product's own artwork sits on,
 * whether that artwork is a 3D model or a photograph with transparency.
 *
 * ## The rule
 *
 * The plate is **the same surface as the cards beside it**, per mode. On the product detail
 * page the right-hand column is `InfoBlock` (`:80`), the default-tag panel and the two
 * accordions, and every one of them paints `theme.palette.background.paper`. So does the
 * plate. Light resolves to `#ffffff`, dark to `#0f0f12` — the zinc card surface, one step
 * above the `#09090b` page, which is what makes a panel read as raised rather than as a
 * hole punched in the page.
 *
 * Read from the TOKEN rather than transcribed to hex, so the plate cannot drift from the
 * cards when the palette moves. That is the whole reason this lives in one file.
 *
 * ## What it is NOT, and why that is worth recording
 *
 * It is not a CONSTANT light plate. A first version of this module pinned the fill to
 * `#f4f5f5` in both modes, on the argument that a product's artwork has a fixed lightness
 * so the surface under it should too — the Shopify/Amazon pattern, and measurably better
 * for contrast (the near-black VERA card is 15.93:1 on `#f4f5f5` and 1.10:1 on `#0f0f12`).
 * It was built, viewed, and **rejected on sight**: against a dark page the bright rectangle
 * read as a sheet of paper dropped onto the UI, and beside zinc cards carrying the same
 * record it was the one element that did not belong to the page.
 *
 * The argument was not wrong about contrast. It was wrong about what the panel IS. This is a
 * card among cards, not a lightbox. A dark product on the dark card surface is lower
 * contrast than it would be on white, and that is the accepted cost of the panel agreeing
 * with its neighbours — a judgement about how the page reads as a whole, which no contrast
 * ratio settles.
 *
 * Recorded so that the constant-plate argument is not re-derived from first principles and
 * re-landed by someone who finds the ratios compelling: it is compelling, it has been tried,
 * and the call went the other way.
 *
 * ## What this still fixes
 *
 * The original ask, which stands: a transparent product photo should not float on a void.
 * Before this, `Slideshow` and the `apps/app` hero painted no background at all, so a cut-out
 * PNG showed the page straight through its own artwork. They now land on the card surface —
 * the same one `ProductModel` renders on, so a photograph and a model in the same hero slot
 * sit on one colour instead of two.
 */

/**
 * The plate's fill — the card surface for this mode.
 *
 * For call sites that need only the colour: a `backgroundColor` inside a larger `sx`, or a
 * nested selector such as Slideshow's `.swiper`. Where you want the whole panel, including
 * its border and radius, use `mediaPlateSx`.
 */
export const mediaPlateColor = (theme: MediaPlateThemeLike): string =>
  theme.palette.background?.paper;

/**
 * The plate as an `sx` fragment: the card surface, inside the same hairline border and
 * radius the panels around it use.
 *
 * Pass `bordered: false` for a full-bleed hero that wants the fill without the frame — the
 * consumer certificate page, where the media runs edge to edge at phone width.
 */
export const mediaPlateSx = (
  theme: MediaPlateThemeLike,
  { bordered = true }: { bordered?: boolean } = {},
) => ({
  backgroundColor: mediaPlateColor(theme),
  ...(bordered
    ? {
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: `${theme.shape?.borderRadius ?? 8}px`,
      }
    : {}),
});
